import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Checkbox, ClickAwayListener, IconButton, Paper,
  Skeleton, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, Tooltip,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { BatchWorkflowListItem } from '../../../types/workflow';
import { BatchWorkflowRow } from '../components/BatchWorkflowRow';
import { ColumnConfigDialog } from '../components/ColumnConfigDialog';
import { usePersistentColumnConfig } from '../../../hooks/usePersistentColumnConfig';
import {
  DEFAULT_COLUMN_ORDER,
  DEFAULT_COLUMN_VISIBILITY,
  WORKFLOW_COLUMNS,
  WORKFLOW_COLUMN_BY_KEY,
  type WorkflowColumnDef,
} from '../workflowColumns';

const STORAGE_KEY = 'workflow-column-config-v1';
/** Legacy visibility-only store (pre-ordering); migrated once so users keep their show/hide choices. */
const LEGACY_VISIBILITY_KEY = 'workflow-column-visibility';
const TOOLTIP_SEEN_KEY = 'workflow-columns-tooltip-seen';

const COLUMN_CONFIG_OPTIONS = {
  storageKey: STORAGE_KEY,
  allKeys: DEFAULT_COLUMN_ORDER as string[],
  defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
  defaultOrder: DEFAULT_COLUMN_ORDER as string[],
  legacyVisibilityKey: LEGACY_VISIBILITY_KEY,
};

interface WorkflowListViewProps {
  batches: BatchWorkflowListItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  selectedIds: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  onRowClick: (batch: BatchWorkflowListItem) => void;
  showActions?: boolean;
  showCheckbox?: boolean;
  onOpenWorkspace?: (batch: BatchWorkflowListItem) => void;
  onReviewSamples?: (batch: BatchWorkflowListItem) => void;
  reviewingBatchId?: number | null;
}


export const WorkflowListView: React.FC<WorkflowListViewProps> = ({
  batches, loading, page, pageSize, totalCount, onPageChange, onPageSizeChange,
  sortBy, sortOrder, onSortChange, selectedIds, onToggleSelect, onSelectAll, onRowClick,
  showActions, showCheckbox = true, onOpenWorkspace, onReviewSamples, reviewingBatchId,
}) => {
  const allSelected = batches.length > 0 && batches.every((b) => selectedIds.has(b.id));
  const someSelected = batches.some((b) => selectedIds.has(b.id)) && !allSelected;

  const handleSort = (col: string) => onSortChange(col, sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc');

  // Column visibility + order, persisted to localStorage (with one-time legacy migration).
  const { visibility, order, orderedVisibleKeys, toggle, move, reorder, reset } =
    usePersistentColumnConfig(COLUMN_CONFIG_OPTIONS);
  const [colDialogOpen, setColDialogOpen] = useState(false);

  // Ordered, visible column defs. Memoized so the reference is stable between renders (the row
  // memo depends on this identity) — it only changes when visibility or order changes.
  const orderedVisibleColumns = useMemo(
    () => orderedVisibleKeys.map((k) => WORKFLOW_COLUMN_BY_KEY[k]).filter(Boolean) as WorkflowColumnDef[],
    [orderedVisibleKeys],
  );

  // Feature discovery tooltip — show once on first visit
  const [showDiscoveryTip, setShowDiscoveryTip] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem(TOOLTIP_SEEN_KEY)) {
      const timer = setTimeout(() => setShowDiscoveryTip(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissDiscoveryTip = useCallback(() => {
    setShowDiscoveryTip(false);
    try { localStorage.setItem(TOOLTIP_SEEN_KEY, '1'); } catch { /* ignore */ }
  }, []);

  const openColumnDialog = useCallback(() => {
    dismissDiscoveryTip();
    setColDialogOpen(true);
  }, [dismissDiscoveryTip]);

  // Base fixed cells: ID + gear + optional checkbox + optional actions, plus the visible columns.
  const columnCount = orderedVisibleColumns.length
    + 2
    + (showCheckbox !== false ? 1 : 0)
    + (showActions ? 1 : 0);

  if (loading) {
    return (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>{Array.from({ length: columnCount }).map((_, i) => <TableCell key={i}><Skeleton width={60} /></TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: columnCount }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper} aria-label="Batch workflow list">
        <Table size="small">
          <TableHead>
            <TableRow sx={(theme) => ({ bgcolor: 'grey.50', ...theme.applyStyles('dark', { bgcolor: 'grey.800' }) })}>
              {showCheckbox && (
                <TableCell padding="checkbox">
                  <Checkbox indeterminate={someSelected} checked={allSelected} onChange={onSelectAll} />
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 600, width: 50 }}>
                <TableSortLabel active={sortBy === 'id'} direction={sortBy === 'id' ? sortOrder : 'asc'} onClick={() => handleSort('id')}>ID</TableSortLabel>
              </TableCell>
              {orderedVisibleColumns.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ fontWeight: 600 }}>
                  {col.sortKey ? (
                    <TableSortLabel
                      active={sortBy === col.sortKey}
                      direction={sortBy === col.sortKey ? sortOrder : 'asc'}
                      onClick={() => handleSort(col.sortKey as string)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
              {showActions && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
              <TableCell padding="checkbox" sx={{ pr: 1 }}>
                {showDiscoveryTip ? (
                  <ClickAwayListener onClickAway={dismissDiscoveryTip}>
                    <Tooltip
                      title="Show, hide or reorder columns"
                      open
                      arrow
                      placement="left"
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: 'primary.main',
                            fontSize: '0.8rem',
                            '& .MuiTooltip-arrow': { color: 'primary.main' },
                          },
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={openColumnDialog}
                        aria-label="Configure columns"
                      >
                        <ViewColumnIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ClickAwayListener>
                ) : (
                  <Tooltip title="Show, hide or reorder columns">
                    <IconButton
                      size="small"
                      onClick={openColumnDialog}
                      aria-label="Configure columns"
                    >
                      <ViewColumnIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {batches.map((batch) => (
              <BatchWorkflowRow
                key={batch.id}
                batch={batch}
                selected={selectedIds.has(batch.id)}
                onToggleSelect={() => onToggleSelect(batch.id)}
                onClick={() => onRowClick(batch)}
                showActions={showActions}
                showCheckbox={showCheckbox}
                columns={orderedVisibleColumns}
                onOpenWorkspace={onOpenWorkspace}
                onReviewSamples={onReviewSamples}
                reviewingBatchId={reviewingBatchId}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page - 1}
        onPageChange={(_, newPage) => onPageChange(newPage + 1)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50, 100]}
      />
      <ColumnConfigDialog
        open={colDialogOpen}
        onClose={() => setColDialogOpen(false)}
        columns={WORKFLOW_COLUMNS}
        order={order}
        visibility={visibility}
        onToggle={toggle}
        onMove={move}
        onReorder={reorder}
        onReset={reset}
      />
    </Box>
  );
};

export default WorkflowListView;
